import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/axios'
import { useToast } from 'vue-toastification'

export const useProfileStore = defineStore('profiles', () => {
  const toast = useToast()
  
  // State
  const profiles = ref([])
  const currentProfile = ref(null)
  const loading = ref(false)
  const error = ref(null)
  
  // Getters
  const activeProfiles = computed(() => 
    profiles.value.filter(p => p.status === 'active')
  )
  
  const profileById = computed(() => (id) => 
    profiles.value.find(p => p.id === parseInt(id))
  )
  
  // Actions
  async function fetchProfiles() {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get('/profiles/')
      profiles.value = response.data.profiles
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to fetch profiles'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function fetchProfile(id) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/profiles/${id}`)
      currentProfile.value = response.data.profile
      
      // Update in list
      const index = profiles.value.findIndex(p => p.id === parseInt(id))
      if (index !== -1) {
        profiles.value[index] = response.data.profile
      }
      
      return response.data.profile
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to fetch profile'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function createProfile(profileData) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/profiles/', profileData)
      profiles.value.push(response.data.profile)
      toast.success('Profile created successfully!')
      return response.data.profile
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to create profile'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function updateProfile(id, profileData) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.put(`/profiles/${id}`, profileData)
      
      // Update in list
      const index = profiles.value.findIndex(p => p.id === parseInt(id))
      if (index !== -1) {
        profiles.value[index] = response.data.profile
      }
      
      // Update current if same
      if (currentProfile.value?.id === parseInt(id)) {
        currentProfile.value = response.data.profile
      }
      
      toast.success('Profile updated successfully!')
      return response.data.profile
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to update profile'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function deleteProfile(id) {
    loading.value = true
    error.value = null
    
    try {
      await api.delete(`/profiles/${id}`)
      
      // Remove from list
      profiles.value = profiles.value.filter(p => p.id !== parseInt(id))
      
      // Clear current if same
      if (currentProfile.value?.id === parseInt(id)) {
        currentProfile.value = null
      }
      
      toast.success('Profile deleted successfully!')
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to delete profile'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function pauseProfile(id) {
    return updateProfile(id, { status: 'paused' })
  }
  
  async function activateProfile(id) {
    return updateProfile(id, { status: 'active' })
  }
  
  return {
    // State
    profiles,
    currentProfile,
    loading,
    error,
    
    // Getters
    activeProfiles,
    profileById,
    
    // Actions
    fetchProfiles,
    fetchProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    pauseProfile,
    activateProfile
  }
})
